import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { URL, getLocalDate, getToken } from "../../utils";
import ChartFullBg from "../../components/ChartFullBg";
import ConditionalRender from "../../components/ConditionalRender";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Purchases from "../../components/Purchases";
import PaymentDeclarationModal from "../../components/PaymentDeclarationModal";
import "react-toastify/dist/ReactToastify.css";

export default function Professional() {
  const [currentClient, setCurrentClient] = useState({});
  const [loading, setLoading] = useState(true);
  const [statisticData, setStatisticData] = useState({
    week: {
      data: [],
      labels: [],
    },
    month: {
      data: [],
      labels: [],
    },
    year: {
      data: [],
      labels: [],
    },
  });
  const [purchases, setPurchases] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("week");
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  const { id } = useParams();
  const token = getToken();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Récupérer les données du professionnel
      const [
        response,
        weekDataResponse,
        monthDataResponse,
        yearDataResponse,
        purchaseData,
      ] = await Promise.all([
        axios.get(URL + "Clients/detail-client-professionnel/" + id, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(
          URL + "Declarations/ventes/graphe-achats-by-client-pro_w/" + id,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        ),
        axios.get(
          URL + "Declarations/ventes/graphe-achats-by-client-pro_m/" + id,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        ),
        axios.get(
          URL + "Declarations/ventes/graphe-achats-by-client-pro_y/" + id,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        ),
        axios.get(
          URL + "Declarations/ventes/listes-achats-by-client-pro/" + id,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        ),
      ]);

      const pro = response.data;
      setCurrentClient(pro);

      // Utiliser les vraies données de l'API
      setPurchases(purchaseData.data || []);

      // Pour les données hebdomadaires, regroupons par jour avec format jour + mois
      const weekTemp = { data: [], labels: [] };
      const weekDataGrouped = {};

      weekDataResponse.data.ventes_par_periode.forEach((item) => {
        const date = new Date(item.date_sortie__date);
        const formattedDate = date.toLocaleDateString("fr-FR", {
          day: "numeric",
          month: "long",
        });

        if (!weekDataGrouped[formattedDate]) {
          weekDataGrouped[formattedDate] = 0;
        }
        weekDataGrouped[formattedDate] += item.total;
      });

      // Conversion du dictionnaire en tableaux ordonnés
      Object.keys(weekDataGrouped).forEach((date) => {
        weekTemp.labels.push(date);
        weekTemp.data.push(weekDataGrouped[date]);
      });

      // Pour les données mensuelles, regroupons par jour avec format jour + mois + année
      const monthTemp = { data: [], labels: [] };
      const monthDataGrouped = {};

      monthDataResponse.data.ventes_par_periode.forEach((item) => {
        const date = new Date(item.date_sortie__date);
        const formattedDate = date.toLocaleDateString("fr-FR", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });

        if (!monthDataGrouped[formattedDate]) {
          monthDataGrouped[formattedDate] = 0;
        }
        monthDataGrouped[formattedDate] += item.total;
      });

      // Conversion du dictionnaire en tableaux ordonnés
      // Utilisons les objets Date pour trier correctement
      const dateKeys = Object.keys(monthDataGrouped);
      const sortedDates = dateKeys
        .map((dateStr) => {
          const parts = dateStr.split(" ");
          const day = parseInt(parts[0]);
          const month = [
            "janvier",
            "février",
            "mars",
            "avril",
            "mai",
            "juin",
            "juillet",
            "août",
            "septembre",
            "octobre",
            "novembre",
            "décembre",
          ].indexOf(parts[1]);
          const year = parseInt(parts[2]);
          return {
            dateStr,
            dateObj: new Date(year, month, day),
            value: monthDataGrouped[dateStr],
          };
        })
        .sort((a, b) => a.dateObj - b.dateObj);

      sortedDates.forEach((item) => {
        monthTemp.labels.push(item.dateStr);
        monthTemp.data.push(item.value);
      });

      // Pour les données annuelles, regroupons par mois
      const yearTemp = { data: [], labels: [] };
      const yearDataGrouped = {};

      yearDataResponse.data.ventes_par_periode.forEach((item) => {
        const monthName = new Date(item.mois).toLocaleString("fr-FR", {
          month: "long",
        });

        if (!yearDataGrouped[monthName]) {
          yearDataGrouped[monthName] = 0;
        }
        yearDataGrouped[monthName] += item.total;
      });

      // Définir l'ordre des mois en français
      const monthOrder = [
        "janvier",
        "février",
        "mars",
        "avril",
        "mai",
        "juin",
        "juillet",
        "août",
        "septembre",
        "octobre",
        "novembre",
        "décembre",
      ];

      // Conversion du dictionnaire en tableaux ordonnés selon l'ordre des mois
      monthOrder.forEach((month) => {
        if (yearDataGrouped[month] !== undefined) {
          yearTemp.labels.push(month);
          yearTemp.data.push(yearDataGrouped[month]);
        }
      });

      setStatisticData({
        week: weekTemp,
        month: monthTemp,
        year: yearTemp,
      });
    } catch (error) {
      toast.error("Erreur lors de la récupération des données");
      console.error("Erreur lors de la récupération des données :", error);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-t-4 border-amber-500 rounded-full animate-spin my-4"></div>
          <p className="text-amber-600 font-medium">
            Chargement des données...
          </p>
        </div>
      </div>
    );
  }

  return (
    currentClient && (
      <>
        <ToastContainer />

        <div className="p-4 bg-white m-2 mb-4 rounded-lg shadow-sm">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
            <div className="flex items-center">
              <div className="rounded-full bg-amber-100 p-3 mr-4">
                <svg
                  className="w-6 h-6 text-amber-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-gray-800">
                  {currentClient.nom_entreprise}
                </h1>
                <p className="text-gray-500">
                  {currentClient.secteur_activite === "night_etablissement"
                    ? "Établissement de nuit"
                    : currentClient.secteur_activite === "shop"
                      ? "Boutique"
                      : currentClient.secteur_activite ||
                        "Secteur non spécifié"}
                </p>
              </div>
              {currentClient.is_active ? (
                <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <span className="h-2 w-2 mr-1 bg-green-500 rounded-full"></span>
                  Actif
                </span>
              ) : (
                <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <span className="h-2 w-2 mr-1 bg-red-500 rounded-full"></span>
                  Inactif
                </span>
              )}
            </div>
            <div className="flex mt-4 md:mt-0 gap-3">
              <ConditionalRender requiredPermissions="canEditClient">
                {currentClient.reste_a_payer > 0 && (
                  <button
                    onClick={() => setIsPaymentModalOpen(true)}
                    className="px-4 py-2 rounded-md bg-green-500 text-white hover:bg-green-600 transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Déclarer un paiement
                  </button>
                )}
              </ConditionalRender>
              <ConditionalRender requiredPermissions="canEditClient">
                <Link
                  to={`/clients/professionnels/edit/${id}`}
                  className="px-4 py-2 rounded-md bg-amber-500 text-white hover:bg-amber-600 transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                    />
                  </svg>
                  Modifier
                </Link>
              </ConditionalRender>
            </div>
          </div>

          {/* Statistiques résumées */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-3 relative">
            <div className="bg-gradient-to-r from-amber-50 to-amber-100 p-3 rounded-lg border border-amber-200 flex items-center">
              <div className="p-2 rounded-md bg-amber-500 text-white mr-3">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">Chiffre d'affaire</p>
                <p className="font-bold text-amber-700">
                  {currentClient.chiffre_affaire?.toLocaleString() || 0} FCFA
                </p>
              </div>
            </div>

            {currentClient.reste_a_payer > 0 && (
              <div className="bg-gradient-to-r from-red-50 to-red-100 p-3 rounded-lg border border-red-200 flex items-center">
                <div className="p-2 rounded-md bg-red-500 text-white mr-3">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Reste à payer</p>
                  <p className="font-bold text-red-700">
                    {currentClient.reste_a_payer?.toLocaleString() || 0} FCFA
                  </p>
                </div>
              </div>
            )}

            <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 rounded-lg border border-blue-200 flex items-center">
              <div className="p-2 rounded-md bg-blue-500 text-white mr-3">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">Dernier achat</p>
                <p className="font-bold text-blue-700">
                  {purchases && purchases.length > 0
                    ? getLocalDate(purchases[0].date_sortie)
                    : "Aucun achat"}
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 rounded-lg border border-green-200 flex items-center">
              <div className="p-2 rounded-md bg-green-500 text-white mr-3">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">Date de création</p>
                <p className="font-bold text-green-700">
                  {currentClient.date_creation
                    ? getLocalDate(new Date(currentClient.date_creation))
                    : "-"}
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-3 rounded-lg border border-purple-200 flex items-center">
              <div className="p-2 rounded-md bg-purple-500 text-white mr-3">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">Nombre d'achats</p>
                <p className="font-bold text-purple-700">
                  {purchases?.length || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-6">
          <div className="flex flex-col xl:flex-row gap-6">
            {/* Carte d'informations principales */}
            <div className="w-full lg:flex-[2] bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg">
              <div className="bg-amber-600 p-4 text-white relative">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-amber-500 rounded-full">
                    <svg
                      className="w-8 h-8"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">
                      Informations de l'entreprise
                    </h2>
                  </div>
                </div>
              </div>
              <div className="p-5 space-y-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">
                    Informations générales
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-amber-100 rounded-lg text-amber-600">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">
                          Nom de l'entreprise
                        </p>
                        <p className="font-medium text-gray-800">
                          {currentClient.nom_entreprise || "-"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">
                          Secteur d'activité
                        </p>
                        <p className="font-medium text-gray-800">
                          {currentClient.secteur_activite ===
                          "night_etablissement"
                            ? "Établissement de nuit"
                            : currentClient.secteur_activite === "shop"
                              ? "Boutique"
                              : currentClient.secteur_activite || "-"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-orange-100 rounded-lg text-orange-600">
                        <svg
                          className="w-6 h-6"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
                          />
                          <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M17.8 13.938h-.011a7 7 0 1 0-11.464.144h-.016l.14.171c.1.127.2.251.3.371L12 21l5.13-6.248c.194-.209.374-.429.54-.659l.13-.155Z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Pays</p>
                        <p className="font-medium text-gray-800">
                          {currentClient.country || currentClient.pays || "-"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg text-purple-600">
                        <svg
                          className="w-6 h-6"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="m4 12 8-8 8 8M6 10.5V19a1 1 0 0 0 1 1h3v-3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3h3a1 1 0 0 0 1-1v-8.5"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Adresse</p>
                        <p className="font-medium text-gray-800">
                          {currentClient.adresse || "-"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-green-100 rounded-lg text-green-600">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">
                          Contact téléphonique
                        </p>
                        <p className="font-medium text-gray-800">
                          {currentClient.contact || "-"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border-t pt-5">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">
                    Informations légales
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-red-100 rounded-lg text-red-600">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">
                          Registre de commerce
                        </p>
                        <p className="font-medium text-gray-800">
                          {currentClient.registre_commerce || "-"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-teal-100 rounded-lg text-teal-600">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Carte CFE</p>
                        <p className="font-medium text-gray-800">
                          {currentClient.carte_cfe ? (
                            <a
                              href={URL + currentClient.carte_cfe}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-amber-600 hover:text-amber-700 hover:underline flex items-center gap-1"
                            >
                              Voir la carte
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                />
                              </svg>
                            </a>
                          ) : (
                            "Non disponible"
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-orange-100 rounded-lg text-orange-600">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">
                          Date de création
                        </p>
                        <p className="font-medium text-gray-800">
                          {currentClient.date_creation
                            ? getLocalDate(
                                new Date(currentClient.date_creation),
                              )
                            : "-"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-yellow-100 rounded-lg text-yellow-600">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">
                          Chiffre d'affaire
                        </p>
                        <p className="font-medium text-gray-800">
                          {currentClient.chiffre_affaire?.toLocaleString() || 0}{" "}
                          FCFA
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:flex-[3]">
              <Purchases purchases={purchases} getLocalDate={getLocalDate} />
            </div>
          </div>
          <div className="grid gap-4 flex-grow rounded-md">
            {/* Section graphique avec design amélioré */}
            <div className="w-full p-4 bg-white shadow-md rounded-md transition-all duration-300 hover:shadow-lg">
              <div className="flex item-center justify-between mb-4 border-b pb-3">
                <div>
                  <h2 className="text-xl font-semibold text-gray-800">
                    {
                      {
                        week: "Historique des achats hebdomadaires",
                        month: "Historique des achats mensuels",
                        year: "Historique des achats annuels",
                      }[selectedPeriod]
                    }
                  </h2>
                  <p className="text-sm text-gray-500">
                    Évolution des achats par période
                  </p>
                </div>
                <div className="flex items-center">
                  <span className="text-sm text-gray-500 mr-2">Période :</span>
                  <select
                    className="p-2 border rounded-md bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500 shadow-sm text-sm"
                    name="selectedPeriod"
                    id="selectedPeriod"
                    value={selectedPeriod}
                    onChange={(e) => {
                      setSelectedPeriod(e.target.value);
                    }}
                  >
                    <option value="week">Semaine</option>
                    <option value="month">Mois</option>
                    <option value="year">Année</option>
                  </select>
                </div>
              </div>
              <div className="mt-2 bg-gray-50 p-2 rounded-lg">
                {
                  {
                    week: (
                      <ChartFullBg
                        key={"week-" + Math.random()}
                        data={statisticData.week.data}
                        dataLabels={statisticData.week.labels}
                        colors={["#f97316"]}
                        title="Historique des achats de la semaine"
                        tooltipSuffix=" FCFA"
                        period="week"
                      />
                    ),
                    month: (
                      <ChartFullBg
                        key={"month-" + Math.random()}
                        data={statisticData.month.data}
                        dataLabels={statisticData.month.labels}
                        colors={["#fbbf24"]}
                        title="Historique des achats du mois"
                        tooltipSuffix=" FCFA"
                        period="month"
                      />
                    ),
                    year: (
                      <ChartFullBg
                        key={"year-" + Math.random()}
                        data={statisticData.year.data}
                        dataLabels={statisticData.year.labels.map(
                          (month) =>
                            month.charAt(0).toUpperCase() + month.slice(1),
                        )}
                        colors={["#84cc16"]}
                        title="Historique des achats de l'année"
                        tooltipSuffix=" FCFA"
                        period="year"
                      />
                    ),
                  }[selectedPeriod]
                }
              </div>
              {/* Information supplémentaire sous le graphique */}
              <div className="mt-4 p-3 bg-amber-50 rounded-md border border-amber-100 text-sm text-amber-700 flex items-center">
                <svg
                  className="w-5 h-5 mr-2 text-amber-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>
                  Les données présentées sont basées sur les transactions
                  enregistrées dans le système.
                </span>
              </div>
            </div>
          </div>
        </div>

        <PaymentDeclarationModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          clientId={id}
          resteAPayer={currentClient.reste_a_payer}
          onPaymentSuccess={fetchData}
        />
      </>
    )
  );
}
